import SearchSelect from '../../../components/SearchSelect/SearchSelect';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import GoogleBusinessProfile from '../../../components/GoogleBusinessProfile/GoogleBusinessProfile';
import { useWebviewMode } from '../../../hooks/useWebviewMode';
import ProfileOptimizationRating from '../../../components/GoogleBusinessProfile/ProfileOptimizationRating';
import { Button } from '../../../components/ui/button';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent
} from '../../../components/ui/card';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent
} from '../../../components/ui/tabs';
import { useUser } from '@clerk/clerk-react';
import {
  Search,
  Pencil,
  Building2,
  MapPin,
  Star,
  Phone,
  Clock,
  Settings,
  Briefcase,
  MessageSquare,
  Loader2,
  Save,
  Check,
  X,
  Sparkles
} from 'lucide-react';
import Select from 'react-select';
import axios from 'axios';

import { authSelector } from '../../../store/slices/auth';
import {
  selectedClientSelector,
  setSelectedClient
} from '../../../store/slices/selectedClient';
import { clientsSelector, getClients } from '../../../store/slices/clients';
import { useSelector, useDispatch } from 'react-redux';
import { allowedEmails, API_CLIENTS_NAME, API_KEY } from '../../../utils';
import {
  getGoogleProfileInfo,
  getGoogleProfileBeforeSnapshot
} from '../../../components/GoogleBusinessProfile/services/index';
import './style.css';
import { useHistory } from 'react-router-dom';

const GoogleBusinessProfileSection = () => {
  const history = useHistory();

  const dispatch = useDispatch();
  const { user } = useUser();
  const { userInfo } = useSelector(authSelector);
  const { selectedClient } = useSelector(selectedClientSelector);
  const { customerList } = useSelector(clientsSelector);
  const currentEmail = userInfo?.email;
  const currentRole = userInfo?.role;
  const [isEditing, setIsEditing] = useState(false);
  const [showQualityAgent, setShowQualityAgent] = useState(false);
  const [loadingAgent, setLoadingAgent] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [currSection, setCurrSection] = useState('about');
  const [optimizationPercentage, setOptimizationPercentage] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [initialCriteria, setInitialCriteria] = useState(null);
  const [currentCriteria, setCurrentCriteria] = useState(null);
  const [beforeCriteriaFromBackend, setBeforeCriteriaFromBackend] =
    useState(null);
  const [beforePercentageFromBackend, setBeforePercentageFromBackend] =
    useState(null);
  const [afterCriteriaFromBackend, setAfterCriteriaFromBackend] =
    useState(null);
  const [afterPercentageFromBackend, setAfterPercentageFromBackend] =
    useState(null);
  const googleBusinessProfileRef = useRef();

  // Labels for each optimization criterion
  const CRITERIA_CONFIG = [
    {
      key: 'description',
      passLabel: 'Business description set',
      failLabel: 'Missing business description'
    },
    {
      key: 'serviceArea',
      passLabel: 'Service areas configured',
      failLabel: 'No service areas configured'
    },
    {
      key: 'reviewFrequency',
      passLabel: 'Review frequency ≥ 1/week',
      failLabel: 'Low review frequency (need ≥ 1/week)'
    },
    {
      key: 'googleRating',
      passLabel: 'Google rating ≥ 4 stars',
      failLabel: 'Google rating below 4 stars'
    },
    {
      key: 'serviceList',
      passLabel: 'All services have descriptions',
      failLabel: 'Services missing descriptions'
    },
    {
      key: 'reviewReplies',
      passLabel: 'Replying to reviews',
      failLabel: 'No review replies'
    },
    {
      key: 'socialNetworks',
      passLabel: 'At least 2 social profiles connected',
      failLabel: 'Less than 2 social profiles Linked'
    },
    {
      key: 'postKeywords',
      passLabel: 'Post keywords optimized',
      failLabel: 'Post keywords not configured'
    }
  ];

  // Determine if user is superAdmin based on domain
  const userEmail = user?.primaryEmailAddress?.emailAddress || '';
  const isSuperAdmin =
    userEmail.endsWith('@adlerlocal.com') ||
    userEmail.endsWith('@samxlabs.com') ||
    userEmail.endsWith('@adlersocial.com');

  const isWebviewMode = useWebviewMode();

  // Check if user has "pro" plan in public metadata
  const hasProPlan = user?.publicMetadata?.plan === 'pro';

  // Load clients if not loaded
  useEffect(() => {
    if (!customerList?.length && userInfo?.accessToken) {
      dispatch(getClients());
    }
  }, [dispatch, customerList?.length, userInfo?.accessToken]);

  // Load first client if no client is selected
  useEffect(() => {
    if (!selectedClient && customerList?.length > 0) {
      dispatch(setSelectedClient(customerList[0]));
    }
  }, [selectedClient, customerList, dispatch]);

  // Intersection Observer to detect which section is visible
  useEffect(() => {
    let observer;
    let scrollHandler;

    const sections = [
      'about',
      'location',
      'services',
      'review-frequency',
      'google-rating',
      'quality',
      'contact',
      'hours'
    ];

    const setupObserver = () => {
      // Check if all sections exist before setting up observer
      const allSectionsExist = sections.every(
        (sectionId) => document.getElementById(sectionId) !== null
      );

      if (!allSectionsExist) {
        // Try again after a short delay
        setTimeout(setupObserver, 100);
        return;
      }

      const observerOptions = {
        root: null,
        rootMargin: '-20% 0px -70% 0px',
        threshold: 0
      };

      const observerCallback = (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setCurrSection(entry.target.id);
          }
        });
      };

      observer = new IntersectionObserver(observerCallback, observerOptions);

      sections.forEach((sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
          observer.observe(element);
        }
      });

      // Handle scroll to detect when at bottom (for last section)
      scrollHandler = () => {
        const scrollHeight = document.documentElement.scrollHeight;
        const scrollTop = document.documentElement.scrollTop;
        const clientHeight = document.documentElement.clientHeight;

        // If scrolled to bottom, set last section as active
        if (scrollTop + clientHeight >= scrollHeight - 10) {
          setCurrSection('hours');
        }
      };

      window.addEventListener('scroll', scrollHandler);
    };

    // Start setup after a small delay to ensure content is rendered
    const timeoutId = setTimeout(setupObserver, 100);

    return () => {
      clearTimeout(timeoutId);

      if (observer) {
        sections.forEach((sectionId) => {
          const element = document.getElementById(sectionId);
          if (element) {
            observer.unobserve(element);
          }
        });
        observer.disconnect();
      }

      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler);
      }
    };
  }, [selectedClient?.id]);

  // Styles for search select (shadcn compatible)
  const searchStyle = {
    control: (base) => ({
      ...base,
      border: '1px solid hsl(var(--border))',
      borderRadius: 'calc(var(--radius) + 3px)',
      minHeight: '48px',
      fontSize: '1rem',
      paddingLeft: '3rem',
      backgroundColor: 'hsl(var(--background))',
      '&:hover': {
        borderColor: 'hsl(var(--ring))'
      }
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '2px 0px',
      paddingLeft: '0px'
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: 'hsl(var(--popover))',
      border: '1px solid hsl(var(--border))',
      borderRadius: 'calc(var(--radius) + 3px)',
      zIndex: 9999
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? 'hsl(var(--accent))' : 'transparent',
      color: 'hsl(var(--foreground))',
      fontSize: '1rem',
      '&:active': {
        backgroundColor: 'hsl(var(--accent))'
      }
    }),
    placeholder: (base) => ({
      ...base,
      color: 'hsl(var(--muted-foreground))',
      fontSize: '1rem',
      marginLeft: '0px'
    }),
    singleValue: (base) => ({
      ...base,
      color: 'hsl(var(--foreground))',
      marginLeft: '0px'
    }),
    input: (base) => ({
      ...base,
      color: 'hsl(var(--foreground))',
      margin: '0px',
      paddingTop: '0px',
      paddingBottom: '0px'
    })
  };

  // Get customer list for select
  const getCustomerList = () => {
    if (!customerList?.length) {
      return [];
    }
    return customerList.map((customer) => ({
      ...customer,
      label: customer.clientName,
      value: customer.clientName
    }));
  };

  // Handle change client
  const handleChangeClient = async (selectedOption) => {
    if (!selectedOption) return;

    try {
      // Fetch complete client data from API to ensure all attributes are present
      const response = await axios.get(
        `${API_CLIENTS_NAME}${selectedOption.clientName}`,
        {
          headers: {
            'X-API-KEY': API_KEY,
            Authorization: `bearer ${userInfo.accessToken}`
          }
        }
      );

      if (response?.data) {
        // Update selected client in Redux with complete data including socialnetworks
        dispatch(setSelectedClient(response.data));
      } else {
        // Fallback to using the client from customerList if API call returns no data
        dispatch(setSelectedClient(selectedOption));
      }
    } catch (error) {
      console.error('Error fetching complete client data:', error);
      // Fallback to using the client from customerList if API call fails
      dispatch(setSelectedClient(selectedOption));
    }

    // Force full page reload to ensure all components refresh with new client data
    history.push('/google-business');
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Si estamos cancelando la edición, limpiar campos y recargar datos
      // Necesitamos acceder al componente hijo para limpiar el estado
      if (
        googleBusinessProfileRef.current &&
        googleBusinessProfileRef.current.cancelEdit
      ) {
        googleBusinessProfileRef.current.cancelEdit();
      }
    }
    setIsEditing(!isEditing);
  };

  // Cargar datos del perfil de Google Business Profile
  useEffect(() => {
    const loadProfileData = async () => {
      if (selectedClient?.id) {
        try {
          const profileInfo = await getGoogleProfileInfo(selectedClient.id);
          setProfileData(profileInfo.data);
        } catch (error) {
          console.error('Error loading Google profile info:', error);
          setProfileData(null);
        }
      }
    };

    loadProfileData();
  }, [selectedClient?.id]);

  // Reset criteria snapshot when client changes
  useEffect(() => {
    setInitialCriteria(null);
    setCurrentCriteria(null);
    setBeforeCriteriaFromBackend(null);
    setBeforePercentageFromBackend(null);
    setAfterCriteriaFromBackend(null);
    setAfterPercentageFromBackend(null);
  }, [selectedClient?.id]);

  // Load immutable BEFORE snapshot from backend (if configured)
  useEffect(() => {
    const loadBeforeSnapshot = async () => {
      if (!selectedClient?.id) return;

      const snapshot = await getGoogleProfileBeforeSnapshot(selectedClient.id);

      if (snapshot) {
        setBeforeCriteriaFromBackend(snapshot.beforeCriteria || null);
        setBeforePercentageFromBackend(snapshot.beforePercentage || null);
        setAfterCriteriaFromBackend(snapshot.afterCriteria || null);
        setAfterPercentageFromBackend(snapshot.afterPercentage || null);
        // Use backend's afterComputed as the authoritative current criteria/percentage
        if (snapshot.afterCriteria) {
          setCurrentCriteria(snapshot.afterCriteria);
        }
        if (
          snapshot.afterPercentage !== null &&
          snapshot.afterPercentage !== undefined
        ) {
          setOptimizationPercentage(Math.round(snapshot.afterPercentage));
        }
      }
    };

    loadBeforeSnapshot();
  }, [selectedClient?.id]);

  // Handler para recibir actualizaciones del porcentaje desde GoogleBusinessProfile
  // Solo se usa como fallback cuando el backend no ha respondido aún
  const handleOptimizationChange = useCallback((percentage) => {
    setOptimizationPercentage(percentage);
  }, []);

  // Handler para recibir el estado de cada criterio de optimización desde GoogleBusinessProfile
  // Solo se usa como fallback cuando el backend no ha respondido aún
  const handleCriteriaUpdate = useCallback(
    (criteria) => {
      // Si el backend ya tiene afterCriteria, ignorar el cálculo local
      setCurrentCriteria((prev) =>
        afterCriteriaFromBackend ? prev : criteria
      );
      setInitialCriteria((prev) => {
        if (beforeCriteriaFromBackend) {
          return prev;
        }
        return prev === null ? criteria : prev;
      });
    },
    [beforeCriteriaFromBackend, afterCriteriaFromBackend]
  );

  // Handler para recibir actualizaciones de cambios sin guardar
  const handleChangesUpdate = useCallback((hasChanges) => {
    setHasUnsavedChanges(hasChanges);
  }, []);

  // Handler para guardar cambios
  const handleSave = () => {
    if (
      googleBusinessProfileRef.current &&
      googleBusinessProfileRef.current.saveChanges
    ) {
      googleBusinessProfileRef.current.saveChanges();
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 overflow-x-hidden">
      {/* Page Header */}
      <main className="p-3 sm:p-6 lg:p-10 space-y-6 sm:space-y-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4 mb-4">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground break-words">
              Google Business Profile,{' '}
              {selectedClient ? selectedClient.clientName : ''}
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">
              Manage how your business appears on Google
            </p>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
            {isSuperAdmin && !isWebviewMode && (
              <div className="relative w-72">
                <SearchSelect
                  placeholder="Search"
                  onChange={(client) => handleChangeClient(client)}
                  options={getCustomerList()}
                  hideSelectedOptions={true}
                  controlShouldRenderValue={false}
                  components={{
                    DropdownIndicator: () => null,
                    IndicatorSeparator: () => null
                  }}
                  openMenuOnClick={false}
                  openMenuOnFocus={false}
                />
              </div>
            )}
            {isEditing && hasProPlan && (
              <Button
                size="lg"
                onClick={handleSave}
                disabled={!hasUnsavedChanges}
                style={{ paddingLeft: '2rem', paddingRight: '2rem' }}
                className={`h-11 sm:h-14 w-full sm:w-auto px-4 sm:px-8 text-sm sm:text-lg font-semibold rounded-xl shadow-lg transition-all gap-2 sm:gap-3 ${
                  hasUnsavedChanges
                    ? 'bg-primary hover:bg-primary/90 text-primary-foreground hover:shadow-xl'
                    : 'bg-muted text-muted-foreground cursor-not-allowed opacity-60'
                }`}>
                <Save className="h-5 w-5" />
                Save Changes
              </Button>
            )}
          </div>
        </div>

        {/* Optimization Score & Action Buttons */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 sm:gap-6 p-4 sm:p-6 bg-gradient-to-r from-primary/5 to-primary/10 rounded-2xl border">
          <div className="flex items-start sm:items-center gap-4 sm:gap-6 min-w-0">
            <ProfileOptimizationRating
              percentage={
                afterPercentageFromBackend !== null
                  ? Math.round(afterPercentageFromBackend)
                  : optimizationPercentage
              }
              size="large"
              showLabel={true}
            />
            <div className="min-w-0">
              <div className="text-base sm:text-lg font-semibold text-foreground">
                Profile Optimization
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Complete the suggestions below to improve your visibility
              </p>
            </div>
          </div>
          {hasProPlan && (
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full lg:w-auto">
              <Button
                variant="outline"
                size="lg"
                onClick={handleEditToggle}
                className="h-11 sm:h-14 w-full sm:w-auto px-4 sm:px-6 rounded-xl gap-2 sm:gap-3 text-sm sm:text-base font-semibold min-w-0">
                <Pencil className="h-5 w-5" />
                <span className="whitespace-normal text-center leading-tight">
                  {isEditing ? 'Cancel Edit' : 'Edit Manually'}
                </span>
              </Button>
              <Button
                size="lg"
                onClick={() => {
                  if (
                    googleBusinessProfileRef.current &&
                    googleBusinessProfileRef.current.handleShowQualityAgent
                  ) {
                    googleBusinessProfileRef.current.handleShowQualityAgent();
                  }
                }}
                disabled={isEditing || loadingAgent}
                className="h-11 sm:h-14 w-full sm:w-auto px-4 sm:px-6 rounded-xl gap-2 sm:gap-3 text-sm sm:text-base font-semibold bg-primary hover:bg-primary/90 min-w-0">
                {loadingAgent ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                    />
                  </svg>
                )}
                <span className="whitespace-normal text-center leading-tight">
                  {loadingAgent ? (
                    'Analyzing...'
                  ) : (
                    <>
                      <span className="sm:hidden">AI Optimize</span>
                      <span className="hidden sm:inline lg:hidden">
                        AI Optimize
                      </span>
                      <span className="hidden lg:inline">
                        AI Optimize Profile
                      </span>
                    </>
                  )}
                </span>
              </Button>
            </div>
          )}
        </div>

        {/* Before & After Comparison */}
        {(() => {
          const beforeCriteria = beforeCriteriaFromBackend || initialCriteria;
          const afterCriteria = afterCriteriaFromBackend || currentCriteria;
          const hasLiveData = Boolean(beforeCriteria || afterCriteria);

          const getOrderedCriteriaEntries = (criteriaObj) => {
            if (!criteriaObj || typeof criteriaObj !== 'object') {
              return [];
            }

            const configuredKeys = CRITERIA_CONFIG.map((item) => item.key);
            const criteriaKeys = Object.keys(criteriaObj);

            const orderedKnownKeys = configuredKeys.filter((key) =>
              Object.prototype.hasOwnProperty.call(criteriaObj, key)
            );
            const unknownKeys = criteriaKeys.filter(
              (key) => !configuredKeys.includes(key)
            );

            return [...orderedKnownKeys, ...unknownKeys].map((key) => [
              key,
              criteriaObj[key]
            ]);
          };

          const afterPct =
            afterPercentageFromBackend !== null
              ? Math.round(afterPercentageFromBackend)
              : optimizationPercentage;
          const afterScoreLabel =
            afterPct >= 100
              ? 'Fully Optimized'
              : afterPct >= 80
              ? 'Almost There'
              : afterPct >= 50
              ? 'Good Progress'
              : 'Needs Improvement';
          const afterScoreSubtitle =
            afterPct >= 95
              ? 'Your profile is performing at its best'
              : afterPct >= 80
              ? "A few more improvements and you're done"
              : afterPct >= 50
              ? 'Making progress — keep going!'
              : 'Several areas require attention';
          const afterBg =
            afterPct >= 80
              ? 'bg-green-50 border-green-200'
              : afterPct >= 50
              ? 'bg-amber-50 border-amber-200'
              : 'bg-red-50 border-red-200';
          const afterRingBorder =
            afterPct >= 80
              ? 'border-green-500'
              : afterPct >= 50
              ? 'border-amber-400'
              : 'border-red-400';
          const afterPctText =
            afterPct >= 80
              ? 'text-green-600'
              : afterPct >= 50
              ? 'text-amber-500'
              : 'text-red-500';
          const afterTitleText =
            afterPct >= 80
              ? 'text-green-800'
              : afterPct >= 50
              ? 'text-amber-800'
              : 'text-red-800';
          const afterSubText =
            afterPct >= 80
              ? 'text-green-600'
              : afterPct >= 50
              ? 'text-amber-600'
              : 'text-red-600';

          const beforePct =
            beforePercentageFromBackend !== null
              ? Math.round(beforePercentageFromBackend)
              : beforeCriteria && Object.keys(beforeCriteria).length > 0
              ? Math.round(
                  (Object.values(beforeCriteria).filter(Boolean).length /
                    Object.keys(beforeCriteria).length) *
                    100
                )
              : 0;
          const beforeScoreLabel =
            beforePct >= 100
              ? 'Fully Optimized'
              : beforePct >= 80
              ? 'Almost There'
              : beforePct >= 50
              ? 'Good Progress'
              : 'Needs Improvement';
          const beforeScoreSubtitle =
            beforePct >= 95
              ? 'Your profile is performing at its best'
              : beforePct >= 80
              ? "A few more improvements and you're done"
              : beforePct >= 50
              ? 'Making progress — keep going!'
              : 'Several areas require attention';
          const beforeBg =
            beforePct >= 80
              ? 'bg-green-50 border-green-200'
              : beforePct >= 50
              ? 'bg-amber-50 border-amber-200'
              : 'bg-red-50 border-red-200';
          const beforeRingBorder =
            beforePct >= 80
              ? 'border-green-500'
              : beforePct >= 50
              ? 'border-amber-400'
              : 'border-red-400';
          const beforePctText =
            beforePct >= 80
              ? 'text-green-600'
              : beforePct >= 50
              ? 'text-amber-500'
              : 'text-red-500';
          const beforeTitleText =
            beforePct >= 80
              ? 'text-green-800'
              : beforePct >= 50
              ? 'text-amber-800'
              : 'text-red-800';
          const beforeSubText =
            beforePct >= 80
              ? 'text-green-600'
              : beforePct >= 50
              ? 'text-amber-600'
              : 'text-red-600';

          return (
            <Card
              className="shadow-md rounded-xl sm:rounded-2xl border-0"
              data-has-live-criteria={hasLiveData ? '1' : '0'}>
              <CardHeader className="pb-3 sm:pb-4 pt-4 sm:pt-6 px-3 sm:px-6 lg:px-8">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <CardTitle className="text-lg sm:text-xl lg:text-2xl font-bold">
                      Optimization Before &amp; After
                    </CardTitle>
                    <CardDescription className="text-xs sm:text-base mt-0.5 sm:mt-1">
                      See how your profile improves after optimization
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-3 sm:px-6 lg:px-8 pb-6 sm:pb-8">
                <Tabs defaultValue="before" className="w-full">
                  <TabsList className="w-full sm:w-auto mb-4 sm:mb-6">
                    <TabsTrigger
                      value="before"
                      className="flex-1 sm:flex-none gap-1.5">
                      <X className="h-4 w-4" /> Before
                    </TabsTrigger>
                    <TabsTrigger
                      value="after"
                      className="flex-1 sm:flex-none gap-1.5">
                      <Check className="h-4 w-4" /> After
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent tabValue="before" className="space-y-4">
                    {beforeCriteria ? (
                      <>
                        <div
                          className={`flex items-center gap-4 sm:gap-6 p-4 sm:p-6 rounded-xl border ${beforeBg}`}>
                          <div
                            className={`flex flex-col items-center justify-center h-16 w-16 sm:h-20 sm:w-20 rounded-full border-4 bg-background shrink-0 ${beforeRingBorder}`}>
                            <span
                              className={`text-xl sm:text-2xl font-bold ${beforePctText}`}>
                              {beforePct}%
                            </span>
                          </div>
                          <div className="min-w-0">
                            <h3
                              className={`text-base sm:text-lg font-semibold ${beforeTitleText}`}>
                              {beforeScoreLabel}
                            </h3>
                            <p
                              className={`text-xs sm:text-sm ${beforeSubText}`}>
                              {beforeScoreSubtitle}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {beforeCriteria &&
                          Object.keys(beforeCriteria).length > 0
                            ? getOrderedCriteriaEntries(beforeCriteria).map(
                                ([key, value]) => {
                                  const criterion = CRITERIA_CONFIG.find(
                                    (c) => c.key === key
                                  );
                                  if (!criterion) return null;
                                  const isOk = Boolean(value);
                                  return (
                                    <div
                                      key={key}
                                      className={`flex items-center gap-3 p-3 rounded-lg border ${
                                        isOk
                                          ? 'bg-green-50 border-green-100'
                                          : 'bg-red-50 border-red-100'
                                      }`}>
                                      {isOk ? (
                                        <Check className="h-4 w-4 text-green-600 shrink-0" />
                                      ) : (
                                        <X className="h-4 w-4 text-red-500 shrink-0" />
                                      )}
                                      <span className="text-sm">
                                        {isOk
                                          ? criterion.passLabel
                                          : criterion.failLabel}
                                      </span>
                                    </div>
                                  );
                                }
                              )
                            : null}
                        </div>
                      </>
                    ) : (
                      <div className="rounded-xl border border-muted bg-muted/40 p-4 sm:p-6 text-sm text-muted-foreground">
                        No before snapshot available yet.
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent tabValue="after" className="space-y-4">
                    <div
                      className={`flex items-center gap-4 sm:gap-6 p-4 sm:p-6 rounded-xl border ${afterBg}`}>
                      <div
                        className={`flex flex-col items-center justify-center h-16 w-16 sm:h-20 sm:w-20 rounded-full border-4 bg-background shrink-0 ${afterRingBorder}`}>
                        <span
                          className={`text-xl sm:text-2xl font-bold ${afterPctText}`}>
                          {afterPct}%
                        </span>
                      </div>
                      <div className="min-w-0">
                        <h3
                          className={`text-base sm:text-lg font-semibold ${afterTitleText}`}>
                          {afterScoreLabel}
                        </h3>
                        <p className={`text-xs sm:text-sm ${afterSubText}`}>
                          {afterScoreSubtitle}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {afterCriteria &&
                      Object.keys(afterCriteria).length > 0 ? (
                        getOrderedCriteriaEntries(afterCriteria).map(
                          ([key, value]) => {
                            const criterion = CRITERIA_CONFIG.find(
                              (c) => c.key === key
                            );
                            if (!criterion) return null;
                            const isOk = Boolean(value);
                            return (
                              <div
                                key={key}
                                className={`flex items-center gap-3 p-3 rounded-lg border ${
                                  isOk
                                    ? 'bg-green-50 border-green-100'
                                    : 'bg-red-50 border-red-100'
                                }`}>
                                {isOk ? (
                                  <Check className="h-4 w-4 text-green-600 shrink-0" />
                                ) : (
                                  <X className="h-4 w-4 text-red-500 shrink-0" />
                                )}
                                <span className="text-sm">
                                  {isOk
                                    ? criterion.passLabel
                                    : criterion.failLabel}
                                </span>
                              </div>
                            );
                          }
                        )
                      ) : (
                        <div className="sm:col-span-2 rounded-xl border border-muted bg-muted/40 p-4 text-sm text-muted-foreground">
                          No current criteria available yet.
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          );
        })()}

        {/* Navigation Menu - Sticky */}
        <div className="sticky top-0 z-10 bg-muted/30 backdrop-blur-sm py-2 sm:py-3 -mx-3 px-3 sm:-mx-6 sm:px-6 lg:-mx-10 lg:px-10">
          <div className="flex gap-1.5 sm:gap-2 bg-background/80 border p-1 sm:p-1.5 rounded-xl w-full sm:w-fit shadow-sm overflow-x-auto no-scrollbar">
            {[
              { id: 'about', label: 'About', icon: Building2 },
              { id: 'location', label: 'Location', icon: MapPin },
              { id: 'services', label: 'Services', icon: Briefcase },
              { id: 'review-frequency', label: 'Reviews', icon: MessageSquare },
              { id: 'google-rating', label: 'Rating', icon: Star },
              { id: 'quality', label: 'Profile Quality', icon: Star },
              { id: 'contact', label: 'Contact', icon: Phone },
              { id: 'hours', label: 'Hours', icon: Clock }
            ].map((section) => {
              const Icon = section.icon;
              return (
                <Button
                  key={section.id}
                  variant={currSection === section.id ? 'default' : 'ghost'}
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrSection(section.id);
                    document
                      .getElementById(section.id)
                      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="rounded-lg shrink-0 text-xs sm:text-sm px-2.5 sm:px-3 min-w-0">
                  <Icon className="h-4 w-4 mr-1.5 sm:mr-2 shrink-0" />
                  <span className="whitespace-nowrap">{section.label}</span>
                </Button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <GoogleBusinessProfile
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          showQualityAgent={showQualityAgent}
          setShowQualityAgent={setShowQualityAgent}
          loadingAgent={loadingAgent}
          setLoadingAgent={setLoadingAgent}
          handleShowQualityAgentRef={googleBusinessProfileRef}
          onOptimizationChange={handleOptimizationChange}
          onCriteriaUpdate={handleCriteriaUpdate}
          onChangesUpdate={handleChangesUpdate}
          currSection={currSection}
          setCurrSection={setCurrSection}
          hasProPlan={hasProPlan}
        />
      </main>
    </div>
  );
};

export default GoogleBusinessProfileSection;
